import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, Users, Share2, GitBranch, MessageSquare, X } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { API_BASE as API } from '../config/api';

const ICON_MAP = {
  friend_request: Users,
  friend_accepted: Users,
  language_shared: Share2,
  collab_edit: GitBranch,
  system: MessageSquare,
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(res.data.count);
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?limit=30`, { withCredentials: true });
      setNotifications(res.data);
    } catch {}
    setLoading(false);
  }, []);

  // Poll unread count every 30s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.notif_id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const deleteNotif = async (notifId) => {
    try {
      await axios.delete(`${API}/notifications/${notifId}`, { withCredentials: true });
      const removed = notifications.find(n => n.notif_id === notifId);
      setNotifications(prev => prev.filter(n => n.notif_id !== notifId));
      if (removed && !removed.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleClick = (notif) => {
    if (!notif.read) markRead(notif.notif_id);
    if (notif.data?.conlang_id) {
      navigate(`/conlang/${notif.data.conlang_id}`);
      setOpen(false);
    } else if (notif.type === 'friend_request' || notif.type === 'friend_accepted') {
      navigate('/friends');
      setOpen(false);
    }
  };

  const timeAgo = (isoStr) => {
    const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(prev => !prev)}
        className="relative text-foreground-muted hover:text-foreground"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 bg-background-paper border border-border rounded-lg shadow-xl z-[100] overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="font-heading text-sm font-semibold text-foreground">Notifications</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllRead}
                  className="text-xs text-primary hover:text-primary/80 h-7 px-2"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3.5 h-3.5 mr-1" />
                  Mark all read
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpen(false)}
                className="h-7 w-7 text-foreground-muted"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {loading && notifications.length === 0 && (
              <div className="p-6 text-center text-foreground-muted text-sm">Loading...</div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="p-6 text-center text-foreground-muted text-sm" data-testid="no-notifications">
                No notifications yet
              </div>
            )}
            {notifications.map((notif) => {
              const Icon = ICON_MAP[notif.type] || MessageSquare;
              return (
                <div
                  key={notif.notif_id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-border/50 cursor-pointer transition-colors hover:bg-accent/50 ${
                    !notif.read ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => handleClick(notif)}
                  data-testid={`notification-item-${notif.notif_id}`}
                >
                  <div className={`mt-0.5 p-1.5 rounded-full flex-shrink-0 ${
                    !notif.read ? 'bg-primary/20 text-primary' : 'bg-accent text-foreground-muted'
                  }`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-snug ${!notif.read ? 'text-foreground font-medium' : 'text-foreground-muted'}`}>
                      {notif.message}
                    </p>
                    <span className="text-xs text-foreground-subtle mt-0.5 block">{timeAgo(notif.created_at)}</span>
                  </div>
                  <div className="flex-shrink-0 flex gap-1 mt-0.5">
                    {!notif.read && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markRead(notif.notif_id); }}
                        className="p-1 rounded hover:bg-accent text-foreground-muted hover:text-primary transition-colors"
                        title="Mark as read"
                        data-testid={`mark-read-${notif.notif_id}`}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteNotif(notif.notif_id); }}
                      className="p-1 rounded hover:bg-accent text-foreground-muted hover:text-red-500 transition-colors"
                      title="Delete"
                      data-testid={`delete-notif-${notif.notif_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
